import { Dialog, Transition } from '@headlessui/react';
import { FC, Fragment, ReactNode, useState } from 'react';
import { useStore } from '../../../../hooks/useStore';
import UpdateProfileDrawer from '../../../common/UpdateProfileDrawer';
import {
  Avatar,
  BuildingIcon,
  Heading,
  IconButton,
  LogOutIcon,
  Text,
  UserIcon,
  XIcon,
  useDrawerContext
} from 'stubben-edge-unified-frontend-react-components';
import { useUserProfileLetterAvatarProps } from '@/utils/useAvatarPresentation';
import { useNavigate } from 'react-router-dom';
import { UPDATE_PROFILE_DRAWER } from '@/constants/drawers';

type UserProfileItem = {
  title: string;
  testIdentifier: string;
  link?: string;
  icon?: ReactNode;
  onClick?: () => void;
};

type UserProfileItemGroup = {
  title?: string;
  testIdentifier?: string;
  menuItems: UserProfileItem[];
};

type UserProfileWidgetProps = {
  testIdentifierPrefix?: string;
};

const UserProfileWidget: FC<UserProfileWidgetProps> = ({
  testIdentifierPrefix = 'header-user-profile'
}) => {
  const { authStore } = useStore();
  const user = authStore.user;
  const navigate = useNavigate();
  const drawer = useDrawerContext();
  const userLetterAvatar = useUserProfileLetterAvatarProps(user);
  const [userProfileDrawerOpen, setUserProfileDrawerOpen] = useState(false);

  const userProfileMenu: Array<UserProfileItemGroup> = [
    {
      title: 'Personal account',
      testIdentifier: 'personal-account',
      menuItems: [
        {
          title: 'Personal profile',
          testIdentifier: 'personal-profile',
          icon: <UserIcon size={24} color="#667085" />,
          onClick: () => {
            setUserProfileDrawerOpen(false);
            drawer.open(UPDATE_PROFILE_DRAWER);
          }
        }
      ]
    },
    {
      title: 'Company account',
      testIdentifier: 'company-account',
      menuItems: [
        {
          title: 'Company profile',
          testIdentifier: 'company-profile',
          icon: <BuildingIcon size={24} color="#667085" />,
          onClick: () => navigate('/company')
        }
      ]
    },
    {
      menuItems: [
        {
          title: 'Log out',
          testIdentifier: 'logout',
          icon: <LogOutIcon size={18} color="#667085" />,
          onClick: () => {
            setUserProfileDrawerOpen(false);
            navigate('/logout');
          }
        }
      ]
    }
  ];

  if (!user) {
    return <></>;
  }

  return (
    <>
      <button
        type="button"
        id={`${testIdentifierPrefix}-button`}
        name={`${testIdentifierPrefix}-button`}
        data-testid={`${testIdentifierPrefix}-button`}
        onClick={() => setUserProfileDrawerOpen(true)}
        className="relative"
      >
        <Avatar
          size="md"
          image={user.profile_photo_path || ''}
          variant={user.profile_photo_path ? 'image' : 'letter'}
          label={user.full_name}
          {...userLetterAvatar}
          statusBadge
          testIdentifier={`${testIdentifierPrefix}-status`}
        />
      </button>

      <Transition show={userProfileDrawerOpen} as={Fragment}>
        <Dialog
          as="div"
          onClose={() => setUserProfileDrawerOpen(false)}
          className="relative z-50"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 w-screen overflow-y-auto">
            <div className="flex min-h-full justify-end text-left">
              <Transition.Child
                as={Fragment}
                enter="transition duration-300 ease-out"
                enterFrom="opacity-0 translate-x-full"
                enterTo="opacity-100 translate-x-0"
                leave="transition duration-150"
                leaveFrom="opacity-100 translate-x-0"
                leaveTo="opacity-0 translate-x-full"
              >
                <Dialog.Panel className="fixed bottom-0 right-0 top-0 z-10 flex max-w-xs flex-col rounded-l-lg bg-white shadow-lg">
                  <div className="flex justify-end px-6 py-4">
                    <IconButton
                      variant="tertiary"
                      onClick={() => setUserProfileDrawerOpen(false)}
                      testIdentifier={`${testIdentifierPrefix}-drawer-close`}
                    >
                      <XIcon size={24} />
                    </IconButton>
                  </div>
                  <div className="flex items-center gap-4 px-6 py-4">
                    <div className="shrink-0">
                      <Avatar
                        size="md"
                        image={user.profile_photo_path || ''}
                        variant={user.profile_photo_path ? 'image' : 'letter'}
                        label={user.full_name}
                        {...userLetterAvatar}
                        testIdentifier={`${testIdentifierPrefix}-drawer-avatar`}
                      />
                    </div>
                    <div className="flex max-w-[80%] flex-col">
                      <Text size="lg" bold="semi">
                        {user.full_name}
                      </Text>
                      <Text
                        size="sm"
                        bold="medium"
                        variant="quaternary"
                        className="truncate"
                      >
                        {user.email}
                      </Text>
                    </div>
                  </div>
                  {userProfileMenu.map((section, sectionIndex) =>
                    section.menuItems.map((item, itemIndex) => (
                      <button
                        key={item.title}
                        onClick={() => item.onClick && item.onClick()}
                        data-testid={`${testIdentifierPrefix}-drawer-${item.testIdentifier}`}
                        className={`flex flex-col gap-2 px-6 text-left hover:bg-gray-50 ${
                          sectionIndex === userProfileMenu.length - 1
                            ? 'mt-auto border-t border-gray-200 py-8'
                            : 'py-4'
                        }`}
                      >
                        {section.title && (
                          <Heading
                            size="xxs"
                            testIdentifier={`${testIdentifierPrefix}-drawer-section-${section.testIdentifier}`}
                          >
                            {section.title}
                          </Heading>
                        )}
                        <div className="flex items-center gap-2">
                          {item.icon}
                          <Text size="sm" bold="medium" variant="quaternary">
                            {item.title}
                          </Text>
                        </div>
                      </button>
                    ))
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <UpdateProfileDrawer />
    </>
  );
};

export default UserProfileWidget;
